import { createClient, type SupabaseClient, type User } from "@supabase/supabase-js";
import type { PortalConfig } from "../lib/config.js";

export const PORTAL_AUTH_STORAGE_KEY = "ohf-portal-auth";
export interface SessionStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}
export interface JoinSessionInstaller {
  install: SupabaseClient["auth"]["setSession"];
  isCurrent(): boolean;
  finish(accepted: boolean): Promise<void>;
}
type InstallerFactory = (recipient: User & { email: string }, interrupted: () => boolean) => JoinSessionInstaller;
const installers = new WeakMap<SupabaseClient, InstallerFactory>();

/** Same browser persistence boundary, with memory fallback when storage is unavailable. */
export function createPortalSessionStorage(): SessionStorage {
  const memory = new Map<string, string>();
  let browserStorage: Storage | null = null;
  try {
    const candidate = globalThis.localStorage;
    const probe = "ohf-auth-storage-check";
    candidate.setItem(probe, probe); candidate.removeItem(probe);
    browserStorage = candidate;
  } catch { /* Browser privacy settings can disable localStorage. */ }
  return {
    getItem: key => browserStorage ? browserStorage.getItem(key) : memory.get(key) ?? null,
    setItem: (key, value) => { if (browserStorage) browserStorage.setItem(key, value); else memory.set(key, value); },
    removeItem: key => { if (browserStorage) browserStorage.removeItem(key); else memory.delete(key); },
  };
}

export function registerJoinSessionInstaller(client: SupabaseClient, factory: InstallerFactory): void {
  installers.set(client, factory);
}

export function joinSessionInstaller(client: SupabaseClient, recipient: User & { email: string }, interrupted: () => boolean): JoinSessionInstaller | null {
  return installers.get(client)?.(recipient, interrupted) ?? null;
}

export class JoinSessionChangedError extends Error {
  constructor(readonly result: "interrupted" | "different-account" = "interrupted") { super("Portal session changed during email confirmation"); }
}

/**
 * Auth's setSession performs a network request before saving. Give that operation
 * its own staging storage so its delayed write/removal cannot replace a newer
 * shared account. The only shared write is a synchronous conditional commit.
 */
export function createConditionalJoinInstaller(config: PortalConfig, backing: SessionStorage, recipient: User & { email: string }, interrupted: () => boolean): JoinSessionInstaller {
  const baseline = backing.getItem(PORTAL_AUTH_STORAGE_KEY);
  if (baseline !== null) {
    let baselineUser: { id?: unknown } | undefined;
    try { baselineUser = (JSON.parse(baseline) as { user?: { id?: unknown } }).user; } catch { /* Fail closed below. */ }
    // getSession and this snapshot are separate reads. Another tab may have
    // changed storage before its broadcast arrives; never adopt that account as
    // the expected baseline for the recipient's installation.
    if (baselineUser?.id !== recipient.id) throw new JoinSessionChangedError(typeof baselineUser?.id === "string" ? "different-account" : "interrupted");
  }
  // A failed SDK lookup can broadcast SIGNED_OUT even if its storage removal
  // only touches staged data. A unique channel keeps every installer event
  // isolated; the initiating provider explicitly hydrates the accepted result.
  const installationKey = `ohf-join-installation-${globalThis.crypto.randomUUID()}`;
  const staged = new Map<string, string>();
  let written: string | null = null;
  const storage: SessionStorage = {
    getItem: key => staged.get(key) ?? null,
    setItem: (key, value) => {
      if (key === installationKey) {
        const candidate = JSON.parse(value) as { user?: User };
        const user = candidate.user;
        if (interrupted() || backing.getItem(PORTAL_AUTH_STORAGE_KEY) !== baseline) throw new JoinSessionChangedError();
        if (!user?.id || user.id !== recipient.id || !user.email_confirmed_at || user.is_anonymous
          || user.email?.toLowerCase() !== recipient.email.toLowerCase()) throw new Error("Invalid email confirmation session");
        backing.setItem(PORTAL_AUTH_STORAGE_KEY, value);
        written = value;
      }
      staged.set(key, value);
    },
    // SDK validation failures may remove a session. This client only owns its
    // staging map; no SDK removal is permitted to clear a shared session.
    removeItem: key => { staged.delete(key); },
  };
  const client = createClient(config.supabaseUrl!, config.supabasePublishableKey!, {
    auth: { storage, storageKey: installationKey, persistSession: true, autoRefreshToken: false, detectSessionInUrl: false },
  });
  return {
    install: tokens => client.auth.setSession(tokens),
    isCurrent: () => {
      try { return !interrupted() && written !== null && backing.getItem(PORTAL_AUTH_STORAGE_KEY) === written; }
      catch { return false; }
    },
    finish: async accepted => {
      try {
        // Early conflict/error returns can finish an installer before install()
        // awaited the SDK's initial task. Do not let initialization outlive it.
        await client.auth.initialize();
        if (!accepted && written !== null && backing.getItem(PORTAL_AUTH_STORAGE_KEY) === written) backing.removeItem(PORTAL_AUTH_STORAGE_KEY);
      } finally {
        staged.clear();
        // No signOut: that would revoke the transferred refresh session at Auth.
        await client.auth.dispose();
      }
    },
  };
}
